import { lazy, useState, type ComponentType } from 'react'

// Route pages take no props; `any` keeps class and function components alike assignable.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PageComponent = ComponentType<any>
type Loader = () => Promise<{ default: PageComponent }>

/**
 * React.lazy with a preload() hook. Once the chunk has been preloaded, the page
 * renders synchronously instead of suspending, so the prerendered HTML on first
 * load is replaced in a single commit rather than flashing the Suspense spinner.
 */
export function lazyPage(loader: Loader) {
  let loaded: PageComponent | null = null
  let pending: Promise<unknown> | null = null
  const preload = () => {
    pending ??= loader().then((m) => {
      loaded = m.default
      return m
    })
    return pending
  }
  const Lazy = lazy(() => preload().then(() => ({ default: loaded as PageComponent })))

  function Page() {
    // Decided once per mount so a page never switches component type mid-life.
    const [Component] = useState<PageComponent>(() => loaded ?? Lazy)
    return <Component />
  }
  Page.preload = preload
  return Page
}
